'use client'

import type { DefaultError, QueryKey } from '@tanstack/react-query'
import type { Omit } from '@wagmi/core/internal'
import { useAccount, useChainId, useConfig, useConnections, useConnectorClient } from 'wagmi'
import type { SimulationErrorType } from '../actions/simulation'
import type { TransactionItems } from '../factories'
import {
  type SimulationData,
  type SimulationOptions,
  type SimulationQueryFnData,
  type SimulationQueryKey,
  simulationQueryOptions,
} from '../query'
import type { ConfigParameter, ExtendedConfig, UseQueryParameters, UseQueryReturnType } from '../types'
import { useQuery } from '../utils'

type QueryParameter<
  queryFnData = unknown,
  error = DefaultError,
  data = queryFnData,
  queryKey extends QueryKey = QueryKey,
> = {
  query?:
    | Omit<
        UseQueryParameters<queryFnData, error, data, queryKey>,
        'queryFn' | 'queryHash' | 'queryKey' | 'queryKeyHashFn' | 'throwOnError'
      >
    | undefined
}

export type UseSimulationParameters<
  config extends ExtendedConfig = ExtendedConfig,
  transactions extends TransactionItems = TransactionItems,
  chainId extends config['chains'][number]['id'] | undefined = undefined,
  selectData = SimulationData,
> = SimulationOptions<config, transactions, chainId> &
  ConfigParameter<config> &
  QueryParameter<
    SimulationQueryFnData,
    SimulationErrorType,
    selectData,
    SimulationQueryKey<config, transactions, chainId>
  >

export type UseSimulationReturnType<selectData = SimulationData> = UseQueryReturnType<selectData, SimulationErrorType>

export function useSimulation<
  config extends ExtendedConfig = ExtendedConfig,
  transactions extends TransactionItems = TransactionItems,
  chainId extends config['chains'][number]['id'] | undefined = undefined,
  selectData = SimulationData,
>(
  parameters: UseSimulationParameters<config, transactions, chainId, selectData> = {} as any,
): UseSimulationReturnType<selectData> {
  const { connector, query = {} } = parameters

  // Get config first to pass to other hooks - avoids WagmiProviderNotFoundError in production
  const config = useConfig(parameters)
  const connections = useConnections({ config })
  const { address } = useAccount({ config })
  const { data: connectorClient } = useConnectorClient({
    config,
    connector: connector ?? connections?.[0]?.connector,
    query: { enabled: parameters.account === undefined },
  })
  const chainId = useChainId({ config })
  let account = parameters.account ?? address ?? connectorClient?.account
  if (!account && connections?.[0]?.connector?.id === 'mock') {
    account = connections?.[0].accounts[0]
  }

  const options = simulationQueryOptions<config, transactions, chainId>(config, {
    ...parameters,
    account,
    chainId: parameters.chainId ?? chainId,
  })
  const enabled = Boolean(account && (query.enabled ?? true) && parameters.transactions?.length)

  // @ts-ignore - Type constraint mismatch
  return useQuery({
    ...query,
    ...options,
    enabled,
    refetchOnMount: query.refetchOnMount ?? false,
    retry: false,
  })
}
